import { SWRConfiguration } from "swr";
import useSWRImmutable from "swr/immutable";
import { Order } from "./constants/types";
import { Cookies } from "react-cookie";
import { OrderStatus } from "./constants/orderStatusMap";
import { StoreRegion } from "../types/regions";
import { useUser } from "../controllers/UserController";
import {
  ORDER_HUB_API_BASE_URL,
  ORDER_HUB_PUBLIC_BASE_URL,
} from "../lib/constants/urls";

async function fetcher<T>(input: RequestInfo, init?: RequestInit) {
  const response = await fetch(input, init);

  // Getting the text first and parsing later for both text (error) and json (error or OK) response
  const text = await response.text();

  try {
    const json = JSON.parse(text);
    if (!response.ok) {
      const errorText = json ? (json.error ?? json.data) : response.statusText;

      throw new Error(errorText);
    } else if (!("data" in json)) {
      throw new Error(`Invalid data response from API: ${text}`);
    } else {
      return json.data as T;
    }
  } catch (e) {
    throw e instanceof Error ? e : new Error(text);
  }
}

type RequestMethod = "GET" | "POST" | "PUT" | "DELETE";

export async function callAPI<T = any>(
  path: string[],
  params?: {
    isAuthd?: boolean;
    method?: RequestMethod;
    /** Array values are sent as repeated keys (`?id=1&id=2`) */
    urlParams?: Record<string, string | string[]>;
    args?: any;
  } & RequestInit
): Promise<T> {
  const { urlParams, args, headers: paramHeaders, ...rest } = params ?? {};
  const isAuthd = params?.isAuthd ?? true;
  const urlExtension = urlParams
    ? `?${new URLSearchParams(
        Object.entries(urlParams).flatMap(([key, value]) =>
          (Array.isArray(value) ? value : [value]).map((v) => [key, v])
        )
      )}`
    : "";

  const baseUrl = isAuthd ? ORDER_HUB_API_BASE_URL : ORDER_HUB_PUBLIC_BASE_URL;

  const csrfCookie = isAuthd
    ? new Cookies(document.cookie).get("csrf")
    : undefined;

  const headers = {
    ...(csrfCookie ? { "x-csrf-token": csrfCookie } : {}),
    ...(args ? { "Content-Type": "application/json" } : {}),
    ...paramHeaders,
  };

  const url = `${baseUrl}/${path.join("/")}${urlExtension}`;

  const data = await fetcher<T>(url, {
    body: args ? JSON.stringify(args) : undefined,
    ...rest,
    headers,
    ...(isAuthd ? { credentials: "include" } : {}),
  });

  return data;
}

export function apiPath(name: string): string {
  return `${ORDER_HUB_API_BASE_URL}/${name}`;
}

export function useAuthdAPI<Data = any>(
  api: string | null,
  config?: SWRConfiguration<Data | undefined, Error>
) {
  const { data: user } = useUser();

  // Skip API call if there is no user logged in
  const finalApiPath = api && user ? apiPath(api) : null;

  return useSWRImmutable<Data | undefined, Error>(
    finalApiPath,
    async (input: RequestInfo, init: RequestInit | undefined) => {
      // Deconstruct headers from init so we can optionally add auth headers
      const { headers: initHeaders, ...rest } = init ?? {};

      const csrfCookie = new Cookies(document.cookie).get("csrf");
      const headers = {
        ...(csrfCookie ? { "x-csrf-token": csrfCookie } : {}),
        ...(initHeaders ?? {}),
      };

      return fetcher<Data>(input, {
        ...rest,
        headers,
        credentials: "include",
      });
    },
    config
  );
}

const nonEncodableOrderStatuses = [
  OrderStatus.PaymentPending,
  OrderStatus.Authorized,
];

// Not all regions can support calling the order endpoint (like ARCHIVE)
export function encodeOrderUrl(order: Order): string | undefined {
  if (
    Object.values<string>(StoreRegion).includes(order.region) &&
    // Little hacky, since payment pending status are in Shopify, not OrderDesk
    !nonEncodableOrderStatuses.includes(order.status)
  ) {
    return `${order.region}-${order.id}`;
  }
}
